import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/constant/common'
import flowRuleApi from '@/api/config/flowRuleApi'
import hydraulicDataConfig from '@/api/config/hydraulicDataConfig'
import { getApiErrorMessage } from '@/helpers/notify'
import { FLOW_RULE_DEFAULTS } from '../../HydraulicRule/FlowRulePanel/constants'
import { findCreatedPipeId, newPipeIdFromResponse, sleep } from './pipe-writes'
import { SAVE_REQUEST_GAP_MS } from './constants'

/**
 * SCRUM-1032 — the first mainline, made for the user.
 *
 * Every site has at least one mainline, and it is the row everything else in
 * the setup hangs off: no pipe means no flow rule, no valves, no pumps. Making
 * the user create it by hand is a step with exactly one sensible answer, and
 * the guided setup's job is to not ask those.
 *
 * So an empty pipe list seeds itself: one mainline at 100 l/m with a flow rule
 * on it, ready to have valves and sensors added. Both are editable afterwards
 * and deletable like any other row — this is a starting point, not a fixture.
 *
 * Only in the guided setup. On /config an empty list is a statement about the
 * site, and writing to it unasked is not this screen's business.
 */

export const SEEDED_MAINLINE_NAME = 'Mainline 1'
export const SEEDED_MAINLINE_CAPACITY = 100

interface UseSeedMainlineArgs {
  /** Guided setup, editable, and the pipe list has finished loading empty. */
  enabled: boolean
  interfaceId: number
  onRefetch?: () => Promise<any>
}

/**
 * Interfaces a seed has been started for since the page loaded.
 *
 * Module level, not a ref: the table unmounts on Next and Back, and the seed
 * is a chain of writes that takes minutes on a slow controller. A ref was
 * reset by the remount, so a user who came back inside the cache window —
 * the cached list still empty — got a second "Mainline 1" (SCRUM-1032
 * review). One attempt per interface, whatever the outcome: a retry loop
 * against a failing endpoint would create duplicates the moment it started
 * half-succeeding, and the user can always add the pipe themselves.
 */
const seededInterfaces = new Set<number>()

/** Is the list on the server empty right now — not as the cache last saw it. */
const hasNoPipes = async (interfaceId: number): Promise<boolean> => {
  const { data } = await hydraulicDataConfig.getAll({
    interfaceId,
    PageIndex: 1,
    PageSize: 1,
  })
  const list = data?.data
  return Array.isArray(list) && list.length === 0 && !Number(data?.totalCount)
}

const seedMainline = async (interfaceId: number) => {
  // The cached rows that enabled this can be minutes old: a mainline the
  // user (or an earlier seed) made since then is not in them.
  if (!(await hasNoPipes(interfaceId))) return

  const created = await hydraulicDataConfig.create({
    interfaceId,
    parentId: 0,
    description: SEEDED_MAINLINE_NAME,
    capacity: SEEDED_MAINLINE_CAPACITY,
  })

  // The rule needs the pipe's id. The create response does not reliably
  // name it, so it is read back the same way a staged save does.
  let pipeId = newPipeIdFromResponse(created)
  if (pipeId === null) {
    await sleep(SAVE_REQUEST_GAP_MS)
    pipeId = await findCreatedPipeId({
      interfaceId,
      parentId: 0,
      description: SEEDED_MAINLINE_NAME,
    })
  }

  // The pipe landed even if the id did not: the user can add the rule with
  // the row's own + button.
  if (pipeId !== null) {
    await sleep(SAVE_REQUEST_GAP_MS)
    await flowRuleApi.create({
      ...FLOW_RULE_DEFAULTS,
      interfaceId,
      pipeId,
    })
  }
}

const useSeedMainline = ({
  enabled,
  interfaceId,
  onRefetch,
}: UseSeedMainlineArgs) => {
  const queryClient = useQueryClient()
  // Read when the seed ends, which can be long after the render that started
  // it — and after an unmount, when there is nothing left to refetch into.
  const onRefetchRef = useRef(onRefetch)
  onRefetchRef.current = onRefetch
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!enabled || !interfaceId) return
    if (seededInterfaces.has(interfaceId)) return
    seededInterfaces.add(interfaceId)

    // Not cancelled when `enabled` flips or the table unmounts: the user
    // adding a mainline, or pressing Next, while the pipe is being written
    // used to stop the chain half way — the pipe made, its flow rule not.
    const run = async () => {
      try {
        await seedMainline(interfaceId)
      } catch (error: any) {
        // Not a toast the user can act on — they did not ask for this write.
        // The list simply stays empty and the "+ Add New Mainline" row is
        // there, which is where they were before.
        console.warn(getApiErrorMessage(error))
      } finally {
        // Cached lists go stale whether or not anyone is looking: a return
        // to the step must read the new mainline, not the empty list.
        queryClient.invalidateQueries([queryKeys.flowRule])
        if (mountedRef.current && onRefetchRef.current) {
          await onRefetchRef.current()
        } else {
          queryClient.invalidateQueries([queryKeys.hydraulic])
        }
      }
    }

    void run()
  }, [enabled, interfaceId, queryClient])
}

export default useSeedMainline
